/**
 * Client-list filter state for the Advisory / Paraplanner portals.
 *
 * Pure: no React, Firebase or userTeamService imports so the matcher can run in
 * tests, the admin defaults page and (via the sanitiser) on saved views. The
 * "current step" dimension is built from open WORKFLOW tasks (firm → template →
 * step); Assignee, Due and Workflow dimensions look at workflow tasks too.
 * `legacyStepIds` (retired Advice Journey steps) stays in the stored shape so
 * older saved views still load, but it is always emptied on sanitise.
 *
 * Semantics: AND across dimensions, OR within a dimension. Every dimension
 * except the buckets also has an exclude list (`exclude*` keys, the "Hide
 * clients with" side of a section): a client matching any excluded value is
 * hidden, even when the include side would show them.
 */

import { getCurrentAdviser } from './currentAdviser';
import { getClientNextArDueYmd, isNextArDueForBooking } from './arDueDateMetrics';
import { hasActiveAnnualReviewRun } from './annualReviewRun';

export const CLIENT_LIST_FILTERS_VERSION = 2;

export const DUE_BUCKETS = ['overdue', 'today', 'week', 'none'];
export const DUE_BUCKET_LABELS = Object.freeze({
  overdue: 'Overdue',
  today: 'Due today',
  week: 'Due this week',
  none: 'No due date',
});

/** Next AR due within two months (or overdue) and no Annual Review workflow running. */
export const WORKFLOW_AR_BOOKING_DUE = 'arBookingDue';
export const WORKFLOW_STATUS_TOKENS = ['active', 'none', 'rework', WORKFLOW_AR_BOOKING_DUE];
export const WORKFLOW_STATUS_LABELS = Object.freeze({
  active: 'Has an active workflow',
  none: 'No active workflow',
  rework: 'In rework (sent back)',
  [WORKFLOW_AR_BOOKING_DUE]: 'Due for AR booking',
});
export const WORKFLOW_TEMPLATE_TOKEN_PREFIX = 'template:';

export const ASSIGNEE_ME = 'me';
export const ASSIGNEE_UNASSIGNED = 'unassigned';
export const ASSIGNEE_USER_PREFIX = 'user:';
export const ASSIGNEE_ROLE_PREFIX = 'role:';

/**
 * Bucket toggles: special client sets that live outside the normal list.
 * Archived / DNQ / unreleased-AR are exclusive (on = show only that bucket);
 * test clients are additive (on = include them alongside everyone else).
 */
export const BUCKET_KEYS = ['showArchived', 'showDnq', 'showTestClients', 'showUnreleasedAr'];

export const BUCKET_LABELS = Object.freeze({
  showArchived: 'Show archived clients only',
  showDnq: 'Show DNQ clients only',
  showTestClients: 'Show test clients',
  showUnreleasedAr: 'Show unreleased AR clients only',
});

export const BUCKET_DESCRIPTIONS = Object.freeze({
  showArchived: 'Archived clients are hidden unless this is on; when it is on, only archived clients show.',
  showDnq: 'Clients marked DNQ (Did Not Qualify) are hidden unless this is on; when it is on, only DNQ clients show.',
  showTestClients: 'Include test clients in the list alongside the real ones.',
  showUnreleasedAr: 'Unreleased manually-entered AR clients are their own workspace — the step, assignee, due and workflow filters do not apply to them.',
});

export const BUCKET_CHIP_LABELS = Object.freeze({
  showArchived: 'Archived clients',
  showDnq: 'DNQ clients',
  showTestClients: 'Test clients',
  showUnreleasedAr: 'Unreleased AR clients',
});

/** Include key → its exclude ("Hide clients with") twin. */
export const CLIENT_LIST_EXCLUDE_KEYS = Object.freeze({
  stepKeys: 'excludeStepKeys',
  assignees: 'excludeAssignees',
  due: 'excludeDue',
  workflow: 'excludeWorkflow',
  firms: 'excludeFirms',
  advisers: 'excludeAdvisers',
  marketers: 'excludeMarketers',
});

const EXCLUDE_TO_INCLUDE_KEY = Object.freeze(Object.fromEntries(
  Object.entries(CLIENT_LIST_EXCLUDE_KEYS).map(([include, exclude]) => [exclude, include]),
));

/** The state key a section edits: the include key, or its exclude twin when hiding. */
export function clientListFilterKey(includeKey, excluding = false) {
  return excluding ? (CLIENT_LIST_EXCLUDE_KEYS[includeKey] || includeKey) : includeKey;
}

export const EMPTY_CLIENT_LIST_FILTERS = Object.freeze({
  version: CLIENT_LIST_FILTERS_VERSION,
  stepKeys: [],
  legacyStepIds: [],
  assignees: [],
  due: [],
  workflow: [],
  firms: [],
  advisers: [],
  marketers: [],
  excludeStepKeys: [],
  excludeAssignees: [],
  excludeDue: [],
  excludeWorkflow: [],
  excludeFirms: [],
  excludeAdvisers: [],
  excludeMarketers: [],
  showArchived: false,
  showDnq: false,
  showTestClients: false,
  showUnreleasedAr: false,
});

const ARRAY_KEYS = [
  'stepKeys', 'legacyStepIds', 'assignees', 'due', 'workflow', 'firms', 'advisers', 'marketers',
  ...Object.values(CLIENT_LIST_EXCLUDE_KEYS),
];

const STEP_KEY_SEPARATOR = '::';

export function normEmail(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

export function encodeStepKey({ advisoryTeamId, templateId, templateTaskId }) {
  return [advisoryTeamId || '', templateId || '', templateTaskId || ''].join(STEP_KEY_SEPARATOR);
}

export function decodeStepKey(key) {
  const [advisoryTeamId = '', templateId = '', templateTaskId = ''] = String(key || '').split(STEP_KEY_SEPARATOR);
  return { advisoryTeamId, templateId, templateTaskId };
}

export function encodeTemplateKey(advisoryTeamId, templateId) {
  return [advisoryTeamId || '', templateId || ''].join(STEP_KEY_SEPARATOR);
}

export function decodeTemplateKey(key) {
  const [advisoryTeamId = '', templateId = ''] = String(key || '').split(STEP_KEY_SEPARATOR);
  return { advisoryTeamId, templateId };
}

export function stepKeyForTask(task) {
  if (!task) return '';
  return encodeStepKey({
    advisoryTeamId: task.advisoryTeamId,
    templateId: task.templateId,
    templateTaskId: task.templateTaskId,
  });
}

export function workflowTemplateToken(advisoryTeamId, templateId) {
  return `${WORKFLOW_TEMPLATE_TOKEN_PREFIX}${encodeTemplateKey(advisoryTeamId, templateId)}`;
}

export function assigneeUserToken(email) {
  return `${ASSIGNEE_USER_PREFIX}${normEmail(email)}`;
}

export function assigneeRoleToken(roleKey) {
  return `${ASSIGNEE_ROLE_PREFIX}${String(roleKey || '').trim()}`;
}

function cleanStrings(value, { lower = false } = {}) {
  if (!Array.isArray(value)) return [];
  const out = [];
  const seen = new Set();
  value.forEach((entry) => {
    if (typeof entry !== 'string') return;
    const trimmed = lower ? entry.trim().toLowerCase() : entry.trim();
    if (!trimmed || seen.has(trimmed)) return;
    seen.add(trimmed);
    out.push(trimmed);
  });
  return out;
}

function keepWhenListed(values, validSet) {
  if (!(validSet instanceof Set)) return values;
  return values.filter((v) => validSet.has(v));
}

function sanitizeAssignee(token, validRoleKeys) {
  if (token === ASSIGNEE_ME || token === ASSIGNEE_UNASSIGNED) return token;
  if (token.startsWith(ASSIGNEE_USER_PREFIX)) {
    const email = normEmail(token.slice(ASSIGNEE_USER_PREFIX.length));
    return email ? `${ASSIGNEE_USER_PREFIX}${email}` : null;
  }
  if (token.startsWith(ASSIGNEE_ROLE_PREFIX)) {
    const roleKey = token.slice(ASSIGNEE_ROLE_PREFIX.length).trim();
    if (!roleKey) return null;
    if (validRoleKeys instanceof Set && !validRoleKeys.has(roleKey)) return null;
    return `${ASSIGNEE_ROLE_PREFIX}${roleKey}`;
  }
  return null;
}

function sanitizeWorkflowToken(token, validTemplateKeys) {
  if (WORKFLOW_STATUS_TOKENS.includes(token)) return token;
  if (token.startsWith(WORKFLOW_TEMPLATE_TOKEN_PREFIX)) {
    const key = token.slice(WORKFLOW_TEMPLATE_TOKEN_PREFIX.length).trim();
    if (!key) return null;
    if (validTemplateKeys instanceof Set && !validTemplateKeys.has(key)) return null;
    return `${WORKFLOW_TEMPLATE_TOKEN_PREFIX}${key}`;
  }
  return null;
}

/**
 * Structural clean-up always; pruning against the valid sets only when a set is
 * supplied (templates and tasks arrive after first paint — never prune blind).
 *
 * @param {object} raw
 * @param {{ validStepKeys?: Set, validTemplateKeys?: Set, validFirmIds?: Set, validMarketerIds?: Set, validRoleKeys?: Set }} [ctx]
 */
export function sanitizeClientListFilters(raw, ctx = {}) {
  const source = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const dimensionValues = (key) => {
    const value = source[key];
    switch (EXCLUDE_TO_INCLUDE_KEY[key] || key) {
      case 'stepKeys': return keepWhenListed(cleanStrings(value), ctx.validStepKeys);
      case 'assignees': return [...new Set(cleanStrings(value).map((t) => sanitizeAssignee(t, ctx.validRoleKeys)).filter(Boolean))];
      case 'due': return cleanStrings(value).filter((d) => DUE_BUCKETS.includes(d));
      case 'workflow': return [...new Set(cleanStrings(value).map((t) => sanitizeWorkflowToken(t, ctx.validTemplateKeys)).filter(Boolean))];
      case 'firms': return keepWhenListed(cleanStrings(value), ctx.validFirmIds);
      case 'advisers': return cleanStrings(value, { lower: true });
      case 'marketers': return keepWhenListed(cleanStrings(value), ctx.validMarketerIds);
      default: return [];
    }
  };
  const next = {
    version: CLIENT_LIST_FILTERS_VERSION,
    stepKeys: dimensionValues('stepKeys'),
    // Legacy Advice Journey steps were retired (Sept 2026): stored ticks are dropped.
    legacyStepIds: [],
    assignees: dimensionValues('assignees'),
    due: dimensionValues('due'),
    workflow: dimensionValues('workflow'),
    firms: dimensionValues('firms'),
    advisers: dimensionValues('advisers'),
    marketers: dimensionValues('marketers'),
    ...Object.fromEntries(BUCKET_KEYS.map((key) => [key, source[key] === true])),
  };
  // A value is shown or hidden, never both: the hide side wins.
  Object.entries(CLIENT_LIST_EXCLUDE_KEYS).forEach(([includeKey, excludeKey]) => {
    next[excludeKey] = dimensionValues(excludeKey);
    if (!next[excludeKey].length) return;
    const hidden = new Set(next[excludeKey]);
    next[includeKey] = next[includeKey].filter((v) => !hidden.has(v));
  });
  return next;
}

export function clearClientListFilters() {
  return { ...EMPTY_CLIENT_LIST_FILTERS, ...Object.fromEntries(ARRAY_KEYS.map((k) => [k, []])) };
}

export function isClientListFiltersEmpty(filters) {
  if (!filters) return true;
  if (BUCKET_KEYS.some((key) => filters[key] === true)) return false;
  return ARRAY_KEYS.every((key) => !Array.isArray(filters[key]) || filters[key].length === 0);
}

/**
 * The workflow data the filters read: open tasks (step, assignee, due, rework)
 * and active runs (the other Workflow options). Until it arrives, a filtered
 * list would show too few clients.
 * @returns {{ tasks: boolean, runs: boolean }}
 */
export function clientListFiltersWorkflowNeeds(filters) {
  const f = filters || EMPTY_CLIENT_LIST_FILTERS;
  const workflow = [
    ...(Array.isArray(f.workflow) ? f.workflow : []),
    ...(Array.isArray(f.excludeWorkflow) ? f.excludeWorkflow : []),
  ];
  return {
    tasks: Boolean(
      f.stepKeys?.length || f.assignees?.length || f.due?.length
      || f.excludeStepKeys?.length || f.excludeAssignees?.length || f.excludeDue?.length
      || workflow.includes('rework'),
    ),
    runs: workflow.some((token) => token !== 'rework'),
  };
}

function sortedCopy(list) {
  return [...(Array.isArray(list) ? list : [])].sort();
}

/** Stable string — the same selection in a different order serialises identically. */
export function serializeClientListFilters(filters) {
  const f = filters || EMPTY_CLIENT_LIST_FILTERS;
  return JSON.stringify({
    ...Object.fromEntries(ARRAY_KEYS.map((k) => [k, sortedCopy(f[k])])),
    ...Object.fromEntries(BUCKET_KEYS.map((k) => [k, f[k] === true])),
  });
}

export function areClientListFiltersEqual(a, b) {
  return serializeClientListFilters(a) === serializeClientListFilters(b);
}

export function countClientListFiltersByDimension(filters) {
  const f = filters || EMPTY_CLIENT_LIST_FILTERS;
  const len = (k) => (Array.isArray(f[k]) ? f[k].length : 0);
  // Shown and hidden values both count.
  const both = (k) => len(k) + len(CLIENT_LIST_EXCLUDE_KEYS[k]);
  const counts = {
    steps: both('stepKeys') + len('legacyStepIds'),
    assignee: both('assignees'),
    due: both('due'),
    workflow: both('workflow'),
    firm: both('firms'),
    adviser: both('advisers'),
    marketer: both('marketers'),
    archive: BUCKET_KEYS.filter((key) => f[key] === true).length,
  };
  counts.total = Object.values(counts).reduce((sum, n) => sum + n, 0);
  return counts;
}

/**
 * Old popover / localStorage payloads → v2 state. `marketingFilter` and
 * `advisoryFilter` were single radios ('' or 'all' meaning no filter).
 */
export function migrateLegacyClientListFilters({
  selectedOpenTaskTemplateIds,
  marketingFilter,
  advisoryFilter,
  showArchived,
  showDnq,
  showTestClients,
  showUnreleasedAr,
} = {}) {
  const marketer = typeof marketingFilter === 'string' ? marketingFilter.trim() : '';
  const advisory = typeof advisoryFilter === 'string' ? advisoryFilter.trim() : '';
  return sanitizeClientListFilters({
    legacyStepIds: Array.isArray(selectedOpenTaskTemplateIds) ? selectedOpenTaskTemplateIds : [],
    marketers: marketer && marketer !== 'all' ? [marketer] : [],
    firms: advisory && advisory !== 'all' ? [advisory] : [],
    showArchived: showArchived === true,
    showDnq: showDnq === true,
    showTestClients: showTestClients === true,
    showUnreleasedAr: showUnreleasedAr === true,
  });
}

/** Legacy projection for older consumers (admin defaults v1, paraplanner advisory radio). */
export function projectClientListFiltersToLegacy(filters) {
  const f = sanitizeClientListFilters(filters);
  return {
    selectedOpenTaskTemplateIds: [...f.legacyStepIds],
    marketingFilter: f.marketers[0] || '',
    advisoryFilter: f.firms[0] || '',
    showArchived: f.showArchived,
  };
}

// ---------------------------------------------------------------------------
// Matching
// ---------------------------------------------------------------------------

export function dueBucketForTask(dueDate, { todayYmd, weekEndYmd }) {
  const ymd = typeof dueDate === 'string' ? dueDate.slice(0, 10) : '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return 'none';
  if (todayYmd && ymd < todayYmd) return 'overdue';
  if (todayYmd && ymd === todayYmd) return 'today';
  if (todayYmd && weekEndYmd && ymd > todayYmd && ymd <= weekEndYmd) return 'week';
  return null;
}

function taskDueBuckets(task, ctx) {
  const bucket = dueBucketForTask(task?.dueDate, ctx);
  if (bucket === 'today') return ['today', 'week'];
  return bucket ? [bucket] : [];
}

function taskMatchesAssignee(task, tokens, userEmail) {
  const assignedTo = normEmail(task?.assignedTo);
  return tokens.some((token) => {
    if (token === ASSIGNEE_ME) return Boolean(userEmail) && assignedTo === userEmail;
    if (token === ASSIGNEE_UNASSIGNED) return !assignedTo;
    if (token.startsWith(ASSIGNEE_USER_PREFIX)) return assignedTo === token.slice(ASSIGNEE_USER_PREFIX.length);
    if (token.startsWith(ASSIGNEE_ROLE_PREFIX)) return (task?.assigneeRoleKey || '') === token.slice(ASSIGNEE_ROLE_PREFIX.length);
    return false;
  });
}

function hasTaskAtStep(stepKeys, workflowTasks) {
  const wanted = new Set(stepKeys);
  return workflowTasks.some((t) => wanted.has(stepKeyForTask(t)));
}

function hasTaskForAssignee(tokens, workflowTasks, userEmail) {
  return workflowTasks.some((t) => taskMatchesAssignee(t, tokens, userEmail));
}

function hasTaskDueIn(buckets, workflowTasks, ctx) {
  const wanted = new Set(buckets);
  return workflowTasks.some((t) => taskDueBuckets(t, ctx).some((b) => wanted.has(b)));
}

function matchesWorkflowDimension(client, tokens, workflowTasks, activeRunsByClientId, todayYmd) {
  const runs = activeRunsByClientId?.[client.id] || [];
  return tokens.some((token) => {
    if (token === 'active') return runs.length > 0;
    if (token === 'none') return runs.length === 0;
    if (token === 'rework') return workflowTasks.some((t) => Number(t?.amendmentRound || 0) > 0);
    if (token === WORKFLOW_AR_BOOKING_DUE) {
      return isNextArDueForBooking(getClientNextArDueYmd(client), todayYmd || undefined)
        && !hasActiveAnnualReviewRun(runs);
    }
    if (token.startsWith(WORKFLOW_TEMPLATE_TOKEN_PREFIX)) {
      const { advisoryTeamId, templateId } = decodeTemplateKey(token.slice(WORKFLOW_TEMPLATE_TOKEN_PREFIX.length));
      return runs.some((run) => (run?.advisoryTeamId || '') === advisoryTeamId && (run?.templateId || '') === templateId);
    }
    return false;
  });
}

/**
 * @param {object} client
 * @param {object} filters  sanitised v2 state
 * @param {{ userEmail: string, todayYmd: string, weekEndYmd: string,
 *   openWorkflowTasksByClientId: Record<string, object[]>,
 *   activeRunsByClientId: Record<string, object[]>,
 *   isUnreleasedManualAr?: (client: object) => boolean }} ctx
 *   `isUnreleasedManualAr` is the host-bound userTeamService predicate, so this
 *   module stays free of the team import chain. Without the predicate the
 *   unreleased-AR bucket is simply not applied.
 */
export function clientMatchesClientListFilters(client, filters, ctx = {}) {
  if (!client) return false;
  const f = filters || EMPTY_CLIENT_LIST_FILTERS;
  if (f.showArchived ? client.isArchived !== true : client.isArchived === true) return false;
  if (f.showDnq ? client.isDnq !== true : client.isDnq === true) return false;
  // Test clients are additive rather than a bucket of their own.
  if (f.showTestClients !== true && ctx.includeTestClients !== true && client.isTestClient === true) return false;
  if (typeof ctx.isUnreleasedManualAr === 'function') {
    const unreleased = ctx.isUnreleasedManualAr(client) === true;
    if (f.showUnreleasedAr ? !unreleased : unreleased) return false;
  }

  const assignment = client.assignment || {};
  const firmId = assignment.assignedAdvisory || '';
  const marketerId = assignment.assignedMarketingTeam || '';
  if (f.firms?.length && !f.firms.includes(firmId)) return false;
  if (f.excludeFirms?.length && f.excludeFirms.includes(firmId)) return false;
  if (f.advisers?.length || f.excludeAdvisers?.length) {
    const adviserEmail = getCurrentAdviser(client).email;
    if (f.advisers?.length && !f.advisers.includes(adviserEmail)) return false;
    if (f.excludeAdvisers?.length && f.excludeAdvisers.includes(adviserEmail)) return false;
  }
  if (f.marketers?.length && !f.marketers.includes(marketerId)) return false;
  if (f.excludeMarketers?.length && f.excludeMarketers.includes(marketerId)) return false;

  const workflowTasks = ctx.openWorkflowTasksByClientId?.[client.id] || [];
  if (f.stepKeys?.length && !hasTaskAtStep(f.stepKeys, workflowTasks)) return false;
  if (f.excludeStepKeys?.length && hasTaskAtStep(f.excludeStepKeys, workflowTasks)) return false;

  const userEmail = normEmail(ctx.userEmail);
  if (f.assignees?.length && !hasTaskForAssignee(f.assignees, workflowTasks, userEmail)) return false;
  if (f.excludeAssignees?.length && hasTaskForAssignee(f.excludeAssignees, workflowTasks, userEmail)) return false;

  if (f.due?.length && !hasTaskDueIn(f.due, workflowTasks, ctx)) return false;
  if (f.excludeDue?.length && hasTaskDueIn(f.excludeDue, workflowTasks, ctx)) return false;

  const runsByClientId = ctx.activeRunsByClientId;
  if (f.workflow?.length && !matchesWorkflowDimension(client, f.workflow, workflowTasks, runsByClientId, ctx.todayYmd)) return false;
  if (f.excludeWorkflow?.length && matchesWorkflowDimension(client, f.excludeWorkflow, workflowTasks, runsByClientId, ctx.todayYmd)) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Chips
// ---------------------------------------------------------------------------

const CHIP_DIMENSION_LABELS = Object.freeze({
  steps: 'Step',
  legacy: 'Step',
  assignees: 'Assignee',
  due: 'Due',
  workflow: 'Workflow',
  firms: 'Firm',
  advisers: 'Adviser',
  marketers: 'Marketer',
  archive: 'Bucket',
  hideSteps: 'Hide step',
  hideAssignees: 'Hide assignee',
  hideDue: 'Hide due',
  hideWorkflow: 'Hide workflow',
  hideFirms: 'Hide firm',
  hideAdvisers: 'Hide adviser',
  hideMarketers: 'Hide marketer',
});

/** Chip dimension → filter key; the `hide*` dimensions are the exclude lists. */
const CHIP_DIMENSION_TO_KEY = Object.freeze({
  steps: 'stepKeys',
  legacy: 'legacyStepIds',
  assignees: 'assignees',
  due: 'due',
  workflow: 'workflow',
  firms: 'firms',
  advisers: 'advisers',
  marketers: 'marketers',
  hideSteps: 'excludeStepKeys',
  hideAssignees: 'excludeAssignees',
  hideDue: 'excludeDue',
  hideWorkflow: 'excludeWorkflow',
  hideFirms: 'excludeFirms',
  hideAdvisers: 'excludeAdvisers',
  hideMarketers: 'excludeMarketers',
});

function resolveLabel(resolvers, name, value, fallback) {
  const fn = resolvers?.[name];
  if (typeof fn === 'function') {
    const out = fn(value);
    if (out) return out;
  }
  return fallback;
}

function assigneeChipLabel(token, labels) {
  if (token === ASSIGNEE_ME) return 'Me';
  if (token === ASSIGNEE_UNASSIGNED) return 'Unassigned';
  if (token.startsWith(ASSIGNEE_USER_PREFIX)) {
    const email = token.slice(ASSIGNEE_USER_PREFIX.length);
    return resolveLabel(labels, 'user', email, email);
  }
  if (token.startsWith(ASSIGNEE_ROLE_PREFIX)) {
    const roleKey = token.slice(ASSIGNEE_ROLE_PREFIX.length);
    return resolveLabel(labels, 'role', roleKey, roleKey);
  }
  return token;
}

function workflowChipLabel(token, labels) {
  if (token.startsWith(WORKFLOW_TEMPLATE_TOKEN_PREFIX)) {
    const key = token.slice(WORKFLOW_TEMPLATE_TOKEN_PREFIX.length);
    return `Running: ${resolveLabel(labels, 'template', key, decodeTemplateKey(key).templateId || key)}`;
  }
  return WORKFLOW_STATUS_LABELS[token] || token;
}

/**
 * One chip per active value, shown values first and then hidden ones
 * (`excluded: true`). `labels` resolvers are host-supplied so this util stays
 * free of team/user lookups:
 *   { step(key), user(email), role(key), firm(id), adviser(email), marketer(id), template(key) }
 * @returns {{ id: string, dimension: string, dimensionLabel: string, label: string, excluded: boolean }[]}
 */
export function describeClientListFilterChips(filters, labels = {}) {
  const f = filters || EMPTY_CLIENT_LIST_FILTERS;
  const chips = [];
  const push = (dimension, value, label) => chips.push({
    id: `${dimension}:${value}`,
    dimension,
    dimensionLabel: CHIP_DIMENSION_LABELS[dimension] || dimension,
    label,
    excluded: dimension.startsWith('hide'),
  });
  const dimensions = [
    ['steps', 'hideSteps', (key) => resolveLabel(labels, 'step', key, decodeStepKey(key).templateTaskId || key)],
    ['assignees', 'hideAssignees', (token) => assigneeChipLabel(token, labels)],
    ['due', 'hideDue', (bucket) => DUE_BUCKET_LABELS[bucket] || bucket],
    ['workflow', 'hideWorkflow', (token) => workflowChipLabel(token, labels)],
    ['firms', 'hideFirms', (id) => resolveLabel(labels, 'firm', id, id)],
    ['advisers', 'hideAdvisers', (email) => resolveLabel(labels, 'adviser', email, email)],
    ['marketers', 'hideMarketers', (id) => resolveLabel(labels, 'marketer', id, id)],
  ];

  dimensions.forEach(([dimension, , labelFor]) => {
    (f[CHIP_DIMENSION_TO_KEY[dimension]] || []).forEach((value) => push(dimension, value, labelFor(value)));
  });
  BUCKET_KEYS.forEach((key) => { if (f[key] === true) push('archive', key, BUCKET_CHIP_LABELS[key]); });
  dimensions.forEach(([, dimension, labelFor]) => {
    (f[CHIP_DIMENSION_TO_KEY[dimension]] || []).forEach((value) => push(dimension, value, labelFor(value)));
  });
  return chips;
}

export function removeClientListFilterChip(filters, chipId) {
  const f = filters || EMPTY_CLIENT_LIST_FILTERS;
  const sep = String(chipId || '').indexOf(':');
  if (sep < 0) return f;
  const dimension = chipId.slice(0, sep);
  const value = chipId.slice(sep + 1);
  if (dimension === 'archive') {
    // 'on' is the pre-buckets chip id, when Archive was the only bucket toggle.
    const key = BUCKET_KEYS.includes(value) ? value : (value === 'on' ? 'showArchived' : null);
    return key ? { ...f, [key]: false } : f;
  }
  const key = CHIP_DIMENSION_TO_KEY[dimension];
  if (!key) return f;
  return { ...f, [key]: (f[key] || []).filter((v) => v !== value) };
}

/** The other side of a dimension: include key ↔ exclude key (null for keys without one). */
function oppositeKey(key) {
  return CLIENT_LIST_EXCLUDE_KEYS[key] || EXCLUDE_TO_INCLUDE_KEY[key] || null;
}

/**
 * Toggle helper for checkbox lists: returns a new state with `value` added/removed
 * from `key`. Ticking a value on one side (show / hide) takes it off the other.
 */
export function toggleClientListFilterValue(filters, key, value, checked) {
  const f = filters || EMPTY_CLIENT_LIST_FILTERS;
  const current = Array.isArray(f[key]) ? f[key] : [];
  const has = current.includes(value);
  const shouldHave = typeof checked === 'boolean' ? checked : !has;
  if (shouldHave === has) return f;
  const next = { ...f, [key]: shouldHave ? [...current, value] : current.filter((v) => v !== value) };
  const other = oppositeKey(key);
  if (shouldHave && other && Array.isArray(f[other]) && f[other].includes(value)) {
    next[other] = f[other].filter((v) => v !== value);
  }
  return next;
}

/** Replace a whole list (select all / clear); values set on one side leave the other. */
export function setClientListFilterValues(filters, key, values) {
  const f = filters || EMPTY_CLIENT_LIST_FILTERS;
  const list = Array.isArray(values) ? values : [];
  const next = { ...f, [key]: list };
  const other = oppositeKey(key);
  if (other && Array.isArray(f[other]) && list.length) {
    const taken = new Set(list);
    next[other] = f[other].filter((v) => !taken.has(v));
  }
  return next;
}
